// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — one round of a live race, decoupled from Trystero, the DOM and the clock (driven
// by place()/tick()), which is what makes the host-transfer contract testable without a
// network (tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer untangles its OWN copy of a web that is a pure function
// of the frozen seed, and reaches its OWN game-over (solve it, run the budget out, or run the
// clock out) with no host help. NO web state ever crosses the wire — only a tiny status gossip
// (webs solved + moves + crossings-left as live progress, and on finishing: done, won, moves,
// time). HOST TRANSFER IS A PURE DISPLAY CONCERN; onHostChange is wired regardless. The winner
// is whoever solves in the FEWEST moves (Loom/Snarl) or banks the MOST webs (Cascade).

import { createGame, place as placeGame, drainEvents, tick as tickGame, type GameEvent, type GameState } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  solved: number; // webs solved (progress; the score in Cascade)
  moves: number;
  crossings: number; // crossings remaining on the current web (progress)
  done: boolean; // their round is over
  won: boolean; // finished successfully (solved, or completed the Cascade run)
  ms: number; // finish time
  par: number;
  left: boolean;
}

/** Tiny wire status. */
export interface RaceMsg {
  s?: number; // webs solved (progress / Cascade score)
  m?: number; // moves so far (progress)
  c?: number; // crossings remaining (progress)
  d?: boolean; // done
  w?: boolean; // won (finished successfully)
  fm?: number; // final moves
  ms?: number; // finish time
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export function seatOf(roster: ReadonlyArray<{ id: string }>, selfId: string): number | null {
  const i = roster.findIndex((p) => p.id === selfId);
  return i < 0 ? null : i;
}

/** A debounced one-shot: poke() ensures fn fires once, `ms` after the FIRST poke of a window. */
export function createOneShot(ms: number, fn: () => void): { poke: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    poke() {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  readonly par: number;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.par = this.state.par;
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        solved: 0,
        moves: 0,
        crossings: this.state.crossings,
        done: false,
        won: false,
        ms: 0,
        par: this.par,
        left: false,
      });
    });
  }

  get isHost(): boolean {
    return this.host;
  }
  setHost(v: boolean): void {
    this.host = v;
  }

  private self(): PeerResult | null {
    return this.selfSeat === null ? null : (this.results.get(this.selfSeat) ?? null);
  }

  /** Apply one placement. Spectators no-op. */
  place(i: number, x: number, y: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    placeGame(this.state, i, x, y);
    const events = drainEvents(this.state);
    const me = this.self();
    if (me) {
      me.solved = this.state.solvedCount;
      me.moves = this.state.moves;
      me.crossings = this.state.crossings;
    }
    if (events.some((e) => e.t === 'move')) {
      this.deps.send({ s: this.state.solvedCount, m: this.state.moves, c: this.state.crossings });
      this.deps.onChange();
    }
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  /** Advance the wall-clock. A Cascade timeout ends the round. */
  tick(dtMs: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    tickGame(this.state, dtMs);
    const events = drainEvents(this.state);
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  private endSelf(): void {
    if (this.ended) return;
    this.ended = true;
    const me = this.self();
    if (me) {
      me.done = true;
      me.won = this.state.finished;
      me.moves = this.state.moves;
      me.ms = Math.round(this.state.elapsedMs);
      me.solved = this.state.solvedCount;
      me.crossings = this.state.crossings;
    }
    this.deps.send({
      d: true,
      w: this.state.finished,
      fm: this.state.moves,
      ms: Math.round(this.state.elapsedMs),
      s: this.state.solvedCount,
    });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
  }

  /** A status update from another peer. Validated, never trusted. */
  receive(msg: RaceMsg, from: number): void {
    if (from === this.selfSeat) return;
    const r = this.results.get(from);
    if (!r || r.left) return;
    if (typeof msg.s === 'number') r.solved = Math.max(0, msg.s | 0);
    if (typeof msg.m === 'number') r.moves = Math.max(0, msg.m | 0);
    if (typeof msg.c === 'number') r.crossings = Math.max(0, msg.c | 0);
    if (msg.d) {
      r.done = true;
      r.won = !!msg.w;
      if (typeof msg.fm === 'number') r.moves = Math.max(0, msg.fm | 0);
      if (typeof msg.ms === 'number') r.ms = Math.max(0, msg.ms | 0);
    }
    this.deps.onChange();
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (r && !r.done) {
      r.left = true;
      this.deps.onChange();
    }
  }

  everyoneDone(): boolean {
    for (const r of this.results.values()) if (!r.done && !r.left) return false;
    return true;
  }

  /** The player who should take the win: fewest moves (single) / most webs (Cascade). */
  winner(): PeerResult | null {
    const top = this.ranked().find((r) => !r.left);
    if (!top) return null;
    if (this.mode.stream) return top.solved > 0 ? top : null;
    return top.won ? top : null;
  }

  /**
   * Single-web modes: solvers first (fewest moves, then fastest), then those still going /
   * out of moves (fewer crossings first), departed last. Cascade: most webs, then fewest moves.
   */
  ranked(): PeerResult[] {
    const stream = this.mode.stream;
    return [...this.results.values()].sort((a, b) => {
      if (a.left !== b.left) return a.left ? 1 : -1;
      if (stream) {
        if (a.solved !== b.solved) return b.solved - a.solved;
        return a.moves - b.moves || a.ms - b.ms;
      }
      const rank = (r: PeerResult): number => (r.done && r.won ? 0 : r.done ? 1 : 2);
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (ra === 0) return a.moves - b.moves || a.ms - b.ms;
      return a.crossings - b.crossings || a.seat - b.seat;
    });
  }
}
